import { useState } from 'react'
import { Button } from '@forwardflow/ui-v2'
import { useAgents } from '../../queries/campaign-v2.queries'
import { useCampaignV2Store } from '../../store/editor.store'
import { IRouting } from '../../interfaces/campaign-v2.types'
import {
   CheckBoxDot,
   EmptyNote,
   Initials,
   IStepFooter,
   ListCard,
   ListRow,
   Loading,
   StepFrame,
   TextButton,
} from '../shared'

/** V2 routing stays in the configuration draft and uses the shared Save action. */
export const TeamStep = ({ footer }: { footer: IStepFooter }) => {
   const { config, setConfig } = useCampaignV2Store()
   const { data: agents, isLoading, error } = useAgents()
   const [picking, setPicking] = useState(false)
   if (!config) return null
   const routing = config.routing
   const set = (patch: Partial<IRouting>) =>
      setConfig((c) => ({ ...c, routing: { ...c.routing, ...patch } }))
   const toggleAgent = (id: number) =>
      set({
         enabled: true,
         agentIds: routing.agentIds.includes(id)
            ? routing.agentIds.filter((x) => x !== id)
            : [...routing.agentIds, id],
      })
   const chosen = routing.agentIds.map(
      (id) =>
         agents?.find((a) => a.crmUserId === id) || {
            crmUserId: id,
            name: `Agent ${id}`,
            email: 'Saved agent · details unavailable',
            phone: null,
         }
   )
   return (
      <StepFrame
         title="Agent routing"
         lede="Choose how to connect interested leads to your team."
         footer={footer}
         testId="step-team"
      >
         <label className="cw-field">
            Assignment strategy
            <select
               value={routing.enabled ? routing.strategy : ''}
               onChange={(e) =>
                  set(
                     e.target.value
                        ? {
                             enabled: true,
                             strategy: e.target.value as IRouting['strategy'],
                          }
                        : { enabled: false }
                  )
               }
               data-testid="routing-strategy"
            >
               <option value="">Not enabled</option>
               <option value="round_robin">Round Robin (Least Busy)</option>
               <option value="jump_ball">Jump Ball (Conference)</option>
            </select>
            <span className="cw-muted">
               {routing.enabled
                  ? routing.strategy === 'jump_ball'
                     ? 'Ring selected agents together. The first agent to accept joins the lead.'
                     : 'Route to the least busy selected agent.'
                  : 'Select a strategy and add the agents who should receive calls.'}
            </span>
         </label>
         <ListCard
            title="Selected agents"
            right={
               <Button
                  size="middle"
                  variant="secondary"
                  className="!h-9"
                  onClick={() => setPicking(!picking)}
                  data-testid="select-agents"
               >
                  {picking ? 'Done' : 'Add agents'}
               </Button>
            }
            testId="agents-card"
         >
            {isLoading && !agents && <Loading label="Loading your team" />}
            {error && (
               <EmptyNote>
                  Could not load your team. Saved agent selections are kept.
               </EmptyNote>
            )}
            {(picking ? agents || [] : chosen).map((a) => (
               <ListRow
                  key={a.crmUserId}
                  icon={
                     picking ? (
                        <CheckBoxDot
                           on={routing.agentIds.includes(a.crmUserId)}
                        />
                     ) : (
                        <Initials name={a.name} size={28} />
                     )
                  }
                  title={a.name}
                  sub={a.email || a.phone || undefined}
                  onClick={picking ? () => toggleAgent(a.crmUserId) : undefined}
                  selected={picking && routing.agentIds.includes(a.crmUserId)}
                  right={
                     !picking ? (
                        <TextButton
                           tone="muted"
                           onClick={() =>
                              set({
                                 agentIds: routing.agentIds.filter(
                                    (id) => id !== a.crmUserId
                                 ),
                              })
                           }
                        >
                           Remove
                        </TextButton>
                     ) : undefined
                  }
                  testId={`agent-${a.crmUserId}`}
               />
            ))}
            {!picking && !chosen.length && (
               <EmptyNote>
                  No agents selected. Use Add agents to choose from your team.
               </EmptyNote>
            )}
            {picking && !isLoading && !error && !agents?.length && (
               <EmptyNote>
                  No eligible agents yet. Add and whitelist teammates in
                  Settings → Team.
               </EmptyNote>
            )}
         </ListCard>
      </StepFrame>
   )
}
